import { Link } from "react-router-dom";
import { Clock, MapPin, Package, Users, Briefcase } from "lucide-react";
import { money, imgUrl, timeAgo } from "@/lib/api";
import { DemoBadge, Stars, Tag, Verified } from "@/components/shared/common";

const Cover = ({ src, alt }) => (
  <div className="aspect-[16/10] overflow-hidden rounded-t-xl bg-muted">
    {src ? <img src={imgUrl(src)} alt={alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      : <div className="grid-bg h-full w-full" />}
  </div>
);

export const ServiceCard = ({ s }) => (
  <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`} className="group card-surface card-hover flex flex-col">
    <Cover src={s.images?.[0]} alt={s.title} />
    <div className="flex flex-1 flex-col gap-3 p-5">
      <div className="flex items-center gap-2">
        {s.vendor_avatar && <img src={imgUrl(s.vendor_avatar)} alt="" className="h-6 w-6 rounded-full object-cover" />}
        <span className="truncate text-sm font-medium">{s.vendor_name}</span>
        {s.is_demo && <DemoBadge className="ml-auto" />}
      </div>
      <h3 className="line-clamp-2 font-semibold leading-snug">{s.title}</h3>
      <Stars avg={s.rating_avg} count={s.rating_count} />
      <div className="mt-auto flex items-end justify-between border-t border-border pt-3">
        <span className="flex items-center gap-1 text-xs text-muted-foreground"><Clock className="h-3.5 w-3.5" /> {s.min_delivery_days}d delivery</span>
        <span className="text-sm"><span className="text-muted-foreground">From </span><span className="font-bold">{money(s.min_price_cents)}</span></span>
      </div>
    </div>
  </Link>
);

export const ProductCard = ({ p }) => (
  <Link to={`/products/${p.slug}`} data-testid={`product-card-${p.slug}`} className="group card-surface card-hover flex flex-col">
    <Cover src={p.images?.[0]} alt={p.title} />
    <div className="flex flex-1 flex-col gap-3 p-5">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Package className="h-3.5 w-3.5" /> <span className="capitalize">{p.product_type?.replace("_", " ")}</span> · v{p.version}
        {p.is_demo && <DemoBadge className="ml-auto" />}
      </div>
      <h3 className="line-clamp-2 font-semibold leading-snug">{p.title}</h3>
      <div className="flex flex-wrap gap-1.5">{(p.tech_stack || []).slice(0, 3).map((t) => <Tag key={t}>{t}</Tag>)}</div>
      <div className="mt-auto flex items-end justify-between border-t border-border pt-3">
        <span className="text-xs text-muted-foreground">by {p.vendor_name}</span>
        <span className="font-bold">{money(p.price_cents)}</span>
      </div>
    </div>
  </Link>
);

export const VendorCard = ({ v }) => (
  <Link to={`/store/${v.slug}`} data-testid={`vendor-card-${v.slug}`} className="group card-surface card-hover flex flex-col gap-4 p-6">
    <div className="flex items-start gap-4">
      {v.avatar_url ? <img src={imgUrl(v.avatar_url)} alt={v.display_name} className="h-14 w-14 rounded-xl object-cover" />
        : <div className="grid h-14 w-14 place-items-center rounded-xl bg-accent text-lg font-bold text-accent-foreground">{v.display_name[0]}</div>}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5"><h3 className="truncate font-semibold">{v.display_name}</h3>{v.verified && <Verified />}</div>
        <p className="line-clamp-2 text-sm text-muted-foreground">{v.headline}</p>
      </div>
    </div>
    <div className="flex flex-wrap gap-1.5">{(v.skills || []).slice(0, 4).map((s) => <Tag key={s}>{s}</Tag>)}</div>
    <div className="mt-auto flex items-center justify-between border-t border-border pt-3 text-sm">
      <span className="flex items-center gap-1 text-muted-foreground">
        {v.type === "agency" ? <Users className="h-3.5 w-3.5" /> : <Briefcase className="h-3.5 w-3.5" />}
        <span className="capitalize">{v.type}</span>
        {v.location && <><MapPin className="ml-2 h-3.5 w-3.5" />{v.location}</>}
      </span>
      {v.is_demo ? <DemoBadge /> : <Stars avg={v.rating_avg} count={v.rating_count} />}
    </div>
    {v.hourly_rate_cents > 0 && <p className="text-sm"><span className="font-bold">{money(v.hourly_rate_cents)}</span><span className="text-muted-foreground">/hr</span></p>}
  </Link>
);

export const ProjectCard = ({ p }) => (
  <Link to={`/projects/${p.id}`} data-testid={`project-card-${p.id}`} className="card-surface card-hover flex flex-col gap-3 p-6">
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <span>{timeAgo(p.created_at)}</span> · <span className="capitalize">{p.experience_level}</span> · <span>{p.proposals_count} proposals</span>
      {p.is_demo && <DemoBadge className="ml-auto" />}
    </div>
    <h3 className="font-semibold leading-snug">{p.title}</h3>
    <p className="line-clamp-2 text-sm text-muted-foreground">{p.description}</p>
    <div className="flex flex-wrap gap-1.5">{(p.skills || []).slice(0, 4).map((s) => <Tag key={s}>{s}</Tag>)}</div>
    <div className="mt-auto flex items-center justify-between border-t border-border pt-3 text-sm">
      <span className="font-semibold">{money(p.budget_min_cents)} – {money(p.budget_max_cents)}<span className="font-normal text-muted-foreground"> {p.budget_type === "hourly" ? "/hr" : "fixed"}</span></span>
      <span className="text-muted-foreground">{p.duration}</span>
    </div>
  </Link>
);
